import type { ReactNode } from "react"
import {
  LayoutDashboard,
  Users,
  DollarSign,
  Building2,
  BookOpen,
  Globe,
  Calendar,
  GraduationCap,
  Newspaper,
  Star,
  History,
  UsersRound,
  ArrowUpCircle,
  Sparkles,
  FileSignature,
  ClipboardList,
  MessageCircle,
  TrendingUp,
  Percent,
  Award,
  Wallet,
  ShieldCheck,
  Home,
  Network,
  Filter,
  Receipt,
  FileText,
  User,
} from "lucide-react"

/**
 * UNA sola fuente para la navegación del portal: el menú lateral, la barra inferior del teléfono,
 * la hoja «Más», el título del encabezado y el buscador (Ctrl K) leen de aquí. Antes cada pieza
 * tenía su propia lista y se desincronizaban (títulos «Partner Portal», pestañas que no estaban
 * en el menú…).
 */
export interface NavItem {
  text: string
  icon: ReactNode
  path: string
  /** Resalta el elemento (Dashboard País del gerente de país). */
  highlight?: boolean
}

export interface NavGroup {
  key: string
  label: string
  items: NavItem[]
}

/** Pestaña de la barra inferior; `fab` es el botón central de «Nueva cotización». */
export type NavTab = { label: string; icon: ReactNode; path: string } | { fab: true; label: string; path: string }

export interface NavDef {
  groups: NavGroup[]
  /** Perfil, seguridad, recursos: van al menú del avatar y a la hoja «Más», no al menú lateral. */
  cuenta: NavItem[]
  tabs: NavTab[]
}

const i = (Icon: typeof Home) => <Icon size={18} />

const CUENTA_CD2B: NavItem[] = [
  { text: "Mi perfil", icon: i(User), path: "/organization" },
  { text: "Seguridad y 2FA", icon: i(ShieldCheck), path: "/seguridad" },
  { text: "Recursos", icon: i(BookOpen), path: "/resources" },
]

function navCd2b(partner: any): NavDef {
  const gerente = partner?.rolCd2b === "gerente"
  const groups: NavGroup[] = [
    {
      key: "trabajo",
      label: "Trabajo",
      items: [
        { text: "Inicio", icon: i(Home), path: "/inicio" },
        { text: "Cotizaciones", icon: i(FileSignature), path: "/consultor/cotizaciones" },
        { text: "Clientes", icon: i(Building2), path: "/clientes" },
        { text: "Levantamientos", icon: i(ClipboardList), path: "/consultor/levantamientos" },
        { text: "Consultas", icon: i(MessageCircle), path: "/consultor/consultas" },
      ],
    },
  ]
  if (gerente) {
    groups.push({
      key: "pais",
      label: "Mi país",
      items: [
        { text: "Mi equipo", icon: i(Network), path: "/equipo" },
        { text: "Pipeline del país", icon: i(Filter), path: "/equipo/pipeline" },
      ],
    })
  }
  const dinero: NavItem[] = [
    { text: "Comisiones", icon: i(DollarSign), path: "/commissions" },
    { text: "Retiros", icon: i(Receipt), path: "/commissions/payouts" },
    { text: "Datos fiscales", icon: i(FileText), path: "/commissions/documentos" },
  ]
  if (partner?.programas?.reventaCd2b?.margen) {
    dinero.push(
      { text: "Mi margen", icon: i(Percent), path: "/reventa/margen" },
      { text: "Mis ingresos (margen)", icon: i(Wallet), path: "/reventa/ingresos" },
    )
  }
  groups.push(
    { key: "dinero", label: "Dinero", items: dinero },
    {
      key: "aprende",
      label: "Aprende",
      items: [
        { text: "Mi aprendizaje", icon: i(GraduationCap), path: "/reventa/aprendizaje" },
        { text: "Mi certificación", icon: i(Award), path: "/reventa/certificacion" },
      ],
    },
  )
  const tabs: NavTab[] = gerente
    ? [
        { label: "Inicio", icon: <Home size={22} />, path: "/inicio" },
        { label: "Equipo", icon: <Network size={22} />, path: "/equipo" },
        { fab: true, label: "Nueva cotización", path: "/consultor/cotizaciones/nueva" },
        { label: "Pipeline", icon: <Filter size={22} />, path: "/equipo/pipeline" },
      ]
    : [
        { label: "Inicio", icon: <Home size={22} />, path: "/inicio" },
        { label: "Cotizar", icon: <FileSignature size={22} />, path: "/consultor/cotizaciones" },
        { fab: true, label: "Nueva cotización", path: "/consultor/cotizaciones/nueva" },
        { label: "Clientes", icon: <Building2 size={22} />, path: "/clientes" },
      ]
  return { groups, cuenta: CUENTA_CD2B, tabs }
}

function navLegal(partner: any): NavDef {
  const cm = partner?.organizationType === "country_manager"
  const groups: NavGroup[] = []
  if (cm) {
    groups.push({
      key: "country",
      label: "Gerente de país",
      items: [
        { text: "Dashboard País", icon: i(Globe), path: "/country", highlight: true },
        { text: "Mi Hub", icon: i(Sparkles), path: "/hub-editor" },
        { text: "Capacitaciones", icon: i(Calendar), path: "/events" },
        { text: "Anuncios", icon: i(Newspaper), path: "/announcements" },
        { text: "Casos de éxito", icon: i(Star), path: "/success-stories" },
        { text: "Historial", icon: i(History), path: "/audit-log" },
        { text: "Equipo del Hub", icon: i(UsersRound), path: "/team-granular" },
        { text: "Academy", icon: i(GraduationCap), path: "/academy" },
      ],
    })
  }
  groups.push({
    key: "general",
    label: "General",
    items: [
      { text: "Dashboard", icon: i(LayoutDashboard), path: "/dashboard" },
      { text: "Referidos", icon: i(Users), path: "/referrals" },
      { text: "Comisiones", icon: i(DollarSign), path: "/commissions" },
      { text: "Retiros", icon: i(Receipt), path: "/commissions/payouts" },
    ],
  })
  if (partner && !cm) {
    groups.push({
      key: "acciones",
      label: "Acciones",
      items: [{ text: "Aplicar como gerente de país", icon: i(ArrowUpCircle), path: "/upgrade-request" }],
    })
  }
  const tabs: NavTab[] = cm
    ? [
        { label: "País", icon: <Globe size={22} />, path: "/country" },
        { label: "Hub", icon: <Sparkles size={22} />, path: "/hub-editor" },
        { label: "Referidos", icon: <Users size={22} />, path: "/referrals" },
        { label: "Comisiones", icon: <DollarSign size={22} />, path: "/commissions" },
      ]
    : [
        { label: "Dashboard", icon: <LayoutDashboard size={22} />, path: "/dashboard" },
        { label: "Referidos", icon: <Users size={22} />, path: "/referrals" },
        { label: "Comisiones", icon: <DollarSign size={22} />, path: "/commissions" },
      ]
  return {
    groups,
    cuenta: [
      { text: "Organización", icon: i(Building2), path: "/organization" },
      { text: "Seguridad y 2FA", icon: i(ShieldCheck), path: "/seguridad" },
      { text: "Recursos", icon: i(BookOpen), path: "/resources" },
    ],
    tabs,
  }
}

export function navFor(partner: any): NavDef {
  return partner?.organizationType === "cd2b_consultant" ? navCd2b(partner) : navLegal(partner)
}

/** Todas las rutas navegables (menú + cuenta), para el título y el buscador. */
export function todosLosItems(def: NavDef): NavItem[] {
  return [...def.groups.flatMap((g) => g.items), ...def.cuenta]
}

/**
 * ¿Está activa la ruta? La más larga que coincide gana: «/equipo/pipeline» no enciende «/equipo»,
 * ni «/commissions/payouts» enciende «Comisiones».
 */
export function activoEn(pathname: string, rutas: string[]) {
  const coincide = (p: string) => pathname === p || (p !== "/dashboard" && pathname.startsWith(p + "/"))
  return (path: string) => {
    if (!coincide(path)) return false
    return !rutas.some((otra) => otra !== path && otra.startsWith(path + "/") && coincide(otra))
  }
}

const TITULOS_EXTRA: Record<string, string> = {
  "/consultor/cotizaciones/nueva": "Nueva cotización",
  "/reventa/resumen": "Reventa",
  "/onboarding": "Bienvenida",
  "/page-editor": "Mi Hub",
}

export function tituloDe(pathname: string, def: NavDef) {
  if (TITULOS_EXTRA[pathname]) return TITULOS_EXTRA[pathname]
  const items = todosLosItems(def)
  const exacto = items.find((it) => it.path === pathname)
  if (exacto) return exacto.text
  const prefijo = items
    .filter((it) => it.path !== "/dashboard" && pathname.startsWith(it.path + "/"))
    .sort((a, b) => b.path.length - a.path.length)[0]
  return prefijo ? prefijo.text : "cd-2b"
}

/**
 * Pantallas de DETALLE (una cotización, una empresa, una consulta…): en el teléfono el encabezado
 * muestra la flecha de volver en lugar de solo el título.
 */
export function rutaPadre(pathname: string): string | null {
  const reglas: Array<[RegExp, string]> = [
    [/^\/consultor\/cotizaciones\/[^/]+/, "/consultor/cotizaciones"],
    [/^\/consultor\/levantamientos\/[^/]+/, "/consultor/levantamientos"],
    [/^\/consultor\/consultas\/[^/]+/, "/consultor/consultas"],
    [/^\/clientes\/[^/]+/, "/clientes"],
    [/^\/referrals\/[^/]+/, "/referrals"],
    [/^\/academy\/courses\//, "/academy"],
    [/^\/commissions\/(payouts|documentos)$/, "/commissions"],
    [/^\/equipo\/pipeline$/, "/equipo"],
  ]
  const r = reglas.find(([re]) => re.test(pathname))
  return r ? r[1] : null
}
